import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import Badge from '../../../components/ui/Badge'
import Button from '../../../components/ui/Button'
import { formatDate } from '../../../lib/datetime'
import type { UserProxy } from '../types'
import { proxyUserLabel } from './proxyUserLabel'

interface ColumnOpts {
  t: TFunction
  /** Kaldırma isteği süren satır — yalnız o satırın butonu kilitlenir. */
  busyId: number | null
  onRevoke: (row: UserProxy) => void
}

/** Kişi hücresi: üstte ad soyad, altında küçük punto ile sicil (seçici panelindeki okuma düzeni). */
function personCell(
  name: string | null,
  surname: string | null,
  employeeId: string | null,
  fallback: string,
): ReactElement {
  const label = proxyUserLabel(name, surname)
  return (
    <div className="admin-proxy-person">
      {/* `USERS` satırı silinmiş kişide ad boş gelir — hücre boş kalmasın diye yer tutucu yazılır. */}
      <span className="admin-proxy-person-name">{label || fallback}</span>
      {employeeId && <span className="admin-proxy-person-sicil">{employeeId}</span>}
    </div>
  )
}

/**
 * Vekalet tablosunun sütunları (paylaşılan `ui/DataTable`'a beslenir): Vekalet Veren · Vekalet Verilen ·
 * Vekalet Tarihi · Kaldırma Tarihi · Durum · İşlemler.
 *
 * **Kolon sıralaması yoktur** (`sortField`/`sortable` verilmez): düzen sunucuda sabittir
 * (`GRANT_DATE DESC, ID DESC` — en yeni üstte).
 *
 * "Vekaleti Kaldır" butonu **yalnız Aktif satırlarda** render edilir (disabled değil, hiç yoktur):
 * Pasif kayıtta yapacak bir iş yoktur ve uç zaten 409 döner.
 */
export function userProxyColumns({ t, busyId, onRevoke }: ColumnOpts): ReactElement[] {
  const unknown = t('admin.userProxies.unknownUser')
  return [
    <Column
      key="from"
      header={t('admin.userProxies.columns.from')}
      body={(row: UserProxy) =>
        personCell(row.fromName, row.fromSurname, row.fromEmployeeId, unknown)
      }
    />,
    <Column
      key="to"
      header={t('admin.userProxies.columns.to')}
      body={(row: UserProxy) => personCell(row.toName, row.toSurname, row.toEmployeeId, unknown)}
    />,
    <Column
      key="grantDate"
      header={t('admin.userProxies.columns.grantDate')}
      style={{ width: '12rem' }}
      /*
        Yalnız GÜN gösterilir (kullanıcı isteği 2026-08-19): vekaletin hangi gün verildiği bilgidir,
        saati değil. Damga DB'de tam olarak duruyor — burada yalnız gösterim kırpılır.

        Yerel biçimlendirici (`formatDate`) kullanılır, kurumsal (`*Business*`) olan DEĞİL: kolon Oracle
        `DATE` tipindedir ve dilimsiz serileşir ("2026-08-19T14:30:00"). `new Date(...)` böyle bir metni
        YEREL saat olarak okur — sunucu damgayı zaten Türkiye saatiyle yazdığı için doğru sonuç budur.
        Kurumsal biçimlendirici, dilimsiz metni yerel sanıp bir kez daha kaydırırdı.
      */
      body={(row: UserProxy) => formatDate(row.grantDate)}
    />,
    <Column
      key="revokeDate"
      header={t('admin.userProxies.columns.revokeDate')}
      style={{ width: '12rem' }}
      // Aktif kayıtta boştur — tire yerine boş bırakılır (tabloda gürültü yapmaz).
      // Vekalet Tarihi ile aynı: yalnız GÜN (kullanıcı isteği 2026-08-19). Damga DB'de tam duruyor.
      body={(row: UserProxy) => (row.revokeDate ? formatDate(row.revokeDate) : '')}
    />,
    <Column
      key="status"
      header={t('admin.userProxies.columns.status')}
      style={{ width: '8rem' }}
      body={(row: UserProxy) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('admin.userProxies.status.active')
              : t('admin.userProxies.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('admin.userProxies.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: UserProxy) =>
        row.recordStatus === 1 ? (
          <div className="admin-row-actions">
            <Button
              icon="pi pi-ban"
              rounded
              text
              severity="danger"
              aria-label={t('admin.userProxies.actions.revoke')}
              tooltip={t('admin.userProxies.actions.revoke')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onRevoke(row)}
            />
          </div>
        ) : null
      }
    />,
  ]
}
